import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Los siete escalones de la escala tipográfica del sistema.
 *
 * Viven acá además de en `index.css` porque `tailwind-merge` necesita SABER que
 * son tamaños. El namespace default de Tailwind está apagado (`--text-*:
 * initial`), así que los escalones del namespace default no existen y toda la
 * app usa estos nombres. Si se agrega uno a la spec, va también en esta lista.
 */
export const ESCALONES_DE_TEXTO = [
  'text-micro',
  'text-caption',
  'text-body',
  'text-body-lg',
  'text-title',
  'text-heading',
  'text-display',
] as const;

/**
 * Los dos radios propios del sistema, fuera de la escala de `--radius`.
 *
 * Misma historia que [ESCALONES_DE_TEXTO], por la misma causa: `tailwind-merge`
 * solo conoce los radios del namespace default de Tailwind. Un `rounded-btn`
 * que no reconoce NO entra en conflicto con el `rounded-md` de la clase base,
 * así que sobreviven los dos y en el DOM gana el que el CSS declare último —
 * que no es el que pidió el sitio de uso. El botón queda con radio
 * impredecible según el orden de compilación.
 *
 * Un radio propio nuevo va también en esta lista.
 */
export const RADIOS_PROPIOS = ['rounded-btn', 'rounded-dialog'] as const;

/**
 * `tailwind-merge` con la escala del sistema declarada como TAMAÑOS.
 *
 * Sin esto, `tailwind-merge` no reconoce `text-caption` ni sus hermanos —los
 * inventó el restyle v2— y los clasifica como COLOR de texto, porque `text-*`
 * sin escalón conocido es un color. El efecto es que cualquier clase de tamaño
 * pasada desde el sitio de uso BORRA el color de la variante:
 *
 *   cn('bg-primary text-primary-foreground', 'h-8 px-3 text-caption')
 *     → 'bg-primary h-8 px-3 text-caption'      ← sin color de texto
 *
 * y el botón termina heredando el color del contenedor. Por eso había botones
 * de acento con texto blanco sobre celeste claro —ilegibles— al lado de otros
 * correctos con texto oscuro: la diferencia era si el sitio de uso pasaba o no
 * una clase de tamaño. Con un escalón del namespace default nunca habría
 * pasado: `tailwind-merge` los conoce. El bug entró junto con la escala propia.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Los literales explícitos ganan contra el validador genérico de color,
      // así que declararlos acá los saca del grupo equivocado.
      'font-size': [...ESCALONES_DE_TEXTO],
      // Mismo motivo, otro grupo: sin esto `rounded-btn` no conflictúa con
      // `rounded-md` y el botón queda con dos radios.
      rounded: [...RADIOS_PROPIOS],
    },
  },
});

/**
 * Combina classNames con clsx (lógica condicional) y twMerge (dedupe
 * inteligente de utilidades Tailwind v4). Patrón estándar shadcn/ui.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
